<template>
  <div>
    <div>
      <button
        v-if="canUserEdit"
        class="btn btn-primary smallActionButton mb-2"
        placement="bottom"
        title="Zuschlagsgruppierung bearbeiten"
        @click.stop="OpenModal"
      >
        <i class="fas fa-edit ml-1 mb-1" />
      </button>
    </div>
    <b-modal
      :ref="'editWageSupplementConfiguration_' + zuschlagsConfiguration.id"
      class="w-100"
      hide-footer
      size="xl"
      title="Zuschlagsgruppierung bearbeiten"
    >
      <u-error-handling
        v-if="errorObject"
        :error-object="errorObject"
      />
      <div class="d-block mb-4">
        Bestimmen sie einen Titel für die Gruppierung sowie die benötigten
        Übersetzungen. Wählen sie die Kriterienwerte, für die die
        Zuschlagsdefinition gelten soll. Wählen sie zuerst das Kriterium, danach
        die Listeneinträge.
      </div>
      <div class="row">
        <h5 class="col-4 mt-4">
          Titel
        </h5>
        <v-text-field
          v-model="wageSupplementTitle"
          class="col-7 ml-4"
        />
      </div>
      <div
        v-if="languages.some((x) => x.language == 'de-CH')"
        class="row"
      >
        <div class="col-4" />
        <v-text-field
          v-model="wageSupplementContentDe"
          :rules="[rules.required]"
          class="col-7 ml-4"
          placeholder="Deutsch"
        />
      </div>
      <div
        v-if="languages.some((x) => x.language == 'fr-CH')"
        class="row"
      >
        <div class="col-4" />
        <v-text-field
          v-model="wageSupplementContentFr"
          :rules="[rules.required]"
          class="col-7 ml-4"
          placeholder="Französisch"
        />
      </div>
      <div
        v-if="languages.some((x) => x.language == 'it-CH')"
        class="row"
      >
        <div class="col-4" />
        <v-text-field
          v-model="wageSupplementContentIt"
          :rules="[rules.required]"
          class="col-7 ml-4"
          placeholder="Italienisch"
        />
      </div>
      <div class="row">
        <h5 class="col-4 mt-4">
          Kriterium
        </h5>
        <v-select
          v-model="selectedCriteria"
          :items="criteriaOptions"
          class="col-7 ml-4"
          item-text="title"
          item-value="id"
          return-object
          @change="LoadOptionOfCriteria"
        />
      </div>

      <div class="row">
        <h5 class="col-4 mt-4">
          Listeneinträge
        </h5>
        <v-select
          v-model="selectedCriteriaEntries"
          :items="criteriaEntryOptions"
          class="col-7 ml-4"
          item-text="value"
          item-value="value"
          multiple
          return-object
        />
      </div>

      <b-button
        :disabled="!isValid"
        variant="success"
        @click="SaveNewWageSupplementConfiguration"
      >
        Speichern
      </b-button>
      <b-button
        class="ml-2"
        variant="danger"
        @click="
          hideModal(
            'editWageSupplementConfiguration_' + zuschlagsConfiguration.id,
          )
        "
      >
        Abbrechen
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import { SaveWageSupplementConfiguration } from "../../../../services/minimum.wage.services/zuschlagsrechner/supplementconfigurations";
import { MinimumWageMixin } from "@/components/minimum.wage/mixins/MinimumWageMixin";
import { GetPossibleStructureValues } from "@/services/minimum.wage.services/criteria.minimum.wages.js";

export default {
  mixins: [MinimumWageMixin],
  props: {
    zuschlagsConfiguration: {
      type: Object,
      required: true,
    },
    languages: {
      type: Array,
      default: () => [],
    },
    canUserEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errorObject: null,
      isLoading: true,
      criteriaOptions: [],
      selectedCriteria: null,
      criteriaEntryOptions: [],
      selectedCriteriaEntries: [],
      wageSupplementTitle: "",
      wageSupplementContentDe: "",
      wageSupplementContentFr: "",
      wageSupplementContentIt: "",
      rules: {
        required: (value) => !!value || "Feld ist erforderlich",
      },
    };
  },
  computed: {
    isValid() {
      let isValid = true;

      if (this.wageSupplementTitle === "") {
        isValid = false;
      }

      if (
        this.wageSupplementContentDe === "" &&
        this.languages.some((x) => x.language === "de-CH")
      ) {
        isValid = false;
      }

      if (
        this.wageSupplementContentFr === "" &&
        this.languages.some((x) => x.language === "fr-CH")
      ) {
        isValid = false;
      }

      if (
        this.wageSupplementContentIt === "" &&
        this.languages.some((x) => x.language === "it-CH")
      ) {
        isValid = false;
      }

      return isValid;
    },
  },
  watch: {
    "$store.state.mindestLohnKonfiguration.Criteria": function () {
      this.LoadRelevantCriterias();
      this.SetFieldsFromObject(this.zuschlagsConfiguration);
    },
  },
  created() {
    this.LoadRelevantCriterias();
    this.SetFieldsFromObject(this.zuschlagsConfiguration);
  },
  methods: {
    OpenModal() {
      this.showModal(
        `editWageSupplementConfiguration_${this.zuschlagsConfiguration.id}`,
      );
      this.SetFieldsFromObject(this.zuschlagsConfiguration);
    },
    SetFieldsFromObject(object) {
      this.wageSupplementTitle = object.wageSupplementConfigurationTitle;
      if (object.criteriaListEntries && object.criteriaListEntries.length > 0) {
        let criteriaOfListEntries = this.criteriaOptions.find((crit) =>
          crit.listEntries.some(
            (entry) =>
              entry.id == object.criteriaListEntries[0].criteriaListEntryId,
          ),
        );
        if (criteriaOfListEntries) {
          this.selectedCriteria = criteriaOfListEntries;
          this.LoadOptionOfCriteria(this.selectedCriteria);
          let selectedEntries = this.criteriaEntryOptions.filter((entry) =>
            object.criteriaListEntries.some(
              (x) => x.criteriaListEntryId == entry.criteriaListEntryId,
            ),
          );
          this.selectedCriteriaEntries = selectedEntries;
        }
      }
      object.wageSupplementConfigurationContents.map((content) => {
        if (content.language == "de-CH") {
          this.wageSupplementContentDe =
            content.wageSupplementConfigurationDescription;
        }
        if (content.language == "fr-CH") {
          this.wageSupplementContentFr =
            content.wageSupplementConfigurationDescription;
        }
        if (content.language == "it-CH") {
          this.wageSupplementContentIt =
            content.wageSupplementConfigurationDescription;
        }
      });
    },
    LoadRelevantCriterias() {
      let itemsCopy = JSON.parse(
        JSON.stringify(this.$store.state.mindestLohnKonfiguration.Criteria),
      );
      this.criteriaOptions = itemsCopy.filter(
        (x) => x.listEntries && x.listEntries.length > 0,
      );
    },
    LoadOptionOfCriteria(newValue) {
      this.selectedCriteriaEntries = [];

      GetPossibleStructureValues(
        this.contractId,
        this.versionId,
        this.editionId,
        newValue.criteriaStructureId,
        [],
        true,
        false,
        (response) => {
          if (response) {
            this.criteriaEntryOptions = response;
            this.isLoading = false;
          }
        },
        (error) => {
          this.errorObject = error;
          this.isLoading = false;
          console.error(error);
        },
      );
    },
    SaveNewWageSupplementConfiguration() {
      this.isLoading = true;
      SaveWageSupplementConfiguration(
        this.contractId,
        this.versionId,
        this.editionId,
        this.editionId,
        this.zuschlagsConfiguration.id,
        this.GetWageSupplementObject(),
        (response) => {
          if (response) {
            this.UpdateSubmitted(response);
            this.hideModal(
              `editWageSupplementConfiguration_${
                this.zuschlagsConfiguration.id
              }`,
            );
          }
          this.isLoading = false;
        },
        (error) => {
          console.error(error);
          this.errorObject = error;
          this.isLoading = false;
        },
      );
    },
    GetWageSupplementObject() {
      let zuschlagsConfigurationCopy = JSON.parse(
        JSON.stringify(this.zuschlagsConfiguration),
      );
      zuschlagsConfigurationCopy.wageSupplementConfigurationTitle =
        this.wageSupplementTitle;

      let langContents = [];

      if (this.wageSupplementContentDe) {
        langContents.push({
          id: 0,
          wageSupplementConfigurationId: zuschlagsConfigurationCopy.id,
          wageSupplementConfigurationDescription: this.wageSupplementContentDe,
          language: "de-CH",
        });
      }
      if (this.wageSupplementContentFr) {
        langContents.push({
          id: 0,
          wageSupplementConfigurationId: zuschlagsConfigurationCopy.id,
          wageSupplementConfigurationDescription: this.wageSupplementContentFr,
          language: "fr-CH",
        });
      }
      if (this.wageSupplementContentIt) {
        langContents.push({
          id: 0,
          wageSupplementConfigurationId: zuschlagsConfigurationCopy.id,
          wageSupplementConfigurationDescription: this.wageSupplementContentIt,
          language: "it-CH",
        });
      }

      zuschlagsConfigurationCopy.wageSupplementConfigurationContents =
        langContents;

      zuschlagsConfigurationCopy.criteriaListEntries = [];
      this.selectedCriteriaEntries.map((entry) => {
        zuschlagsConfigurationCopy.criteriaListEntries.push({
          id: 0,
          wageSupplementConfigurationId: 0,
          criteriaListEntryId: entry.criteriaListEntryId,
        });
      });

      return zuschlagsConfigurationCopy;
    },
    UpdateSubmitted(updatedObject) {
      this.$emit("updated", updatedObject);
    },
    showModal(modalId) {
      this.$refs[modalId].show();
    },
    hideModal(modalRef) {
      if (this.$refs[modalRef]) {
        this.$refs[modalRef].hide();
      }
    },
  },
};
</script>

<style></style>
